import {
    Flex,
    FormControl,
    FormHelperText,
    Image,
    useColorMode,
} from '@chakra-ui/react';

import _ from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { PageUrls, getLoginFormPagePath } from '../../App';
import Button from '../../components/button';
import Heading from '../../components/heading';
import Input from '../../components/input';
import InputPassword from '../../components/input-password';
import Layout from '../../components/layout';
import Text from '../../components/text';
import { useBookLogin } from '../../hooks';
import { LoginStatuses } from '../../hooks/useBookLogin';
import { useSession } from '../../hooks/useSession';
import Logos from '../../images/logos';
import {
    IBookLog,
    IBookRegionLog,
    IUserLog,
    logBookLoginResponse,
    logBookLoginSubmit,
    logOpenAffiliateLink,
    logUnsupported,
} from '../../services/datadog';
import SwalService from '../../services/swal';
import {
    IBook,
    IUserAccount,
    IUserAccountRegion,
    TBookAbbrvs,
    UnsupportedReason,
} from '../../types';
import UnverifiedResponseModal from './unverified-response-modal';

export function getInputLabel(abbr: string) {
    switch (abbr) {
        case 'fb':
        case 'st':
        case 'wb':
            return 'Username';
        case 'fl':
            return 'Phone Number';
        default:
            return 'Email';
    }
}

export interface IUserLoginProps {
    getUnsupportedReason: (
        book: IBook | undefined,
        userAccountRegion: IUserAccountRegion | undefined
    ) => UnsupportedReason;
    extensionVersion: string | null;
    versionChecked: boolean;
}

export default function UserLogin(props: IUserLoginProps) {
    const { getUnsupportedReason, extensionVersion, versionChecked } = props;
    const location = useLocation();
    const pathname = location.pathname;
    const history = useHistory();

    const [isUnverifiedResponseModalOpen, setIsUnverifiedResponseModalOpen] =
        useState(false);
    const session = useSession();
    const { colorMode, toggleColorMode } = useColorMode();

    useEffect(() => {
        if (
            session?.uiMode &&
            session?.uiMode != 'system' &&
            session?.uiMode != colorMode
        )
            toggleColorMode();
    }, []);

    //auto scroll to top of page
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    //Send custom event to indicate redirect url for deeplink books
    useEffect(() => {
        const redirectEvent = new CustomEvent('redirectUrl', {
            detail: {
                url: window.location.href,
            },
        });
        document.dispatchEvent(redirectEvent);
    }, []);

    //Send custom event to initialize browser extension
    useEffect(() => {
        let token = session?.token;
        const initEvent = new CustomEvent('initSharpSportsExtension', {
            detail: {
                internalId: session?.internalId,
                publicKey: token ? token.replace('Token ', '') : '',
                extensionAuthToken: session?.extensionAuthToken,
            },
        });
        document.dispatchEvent(initEvent);
    }, []);

    //Handle loading query param
    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        if (queryParams.has('loading')) {
            queryParams.delete('loading');
            history.replace({
                search: queryParams.toString(),
            });
            checkLoginStatus(true);
        }
    }, []);

    // get variables used for book login from url
    let loginBookAbbr: TBookAbbrvs | undefined = undefined;
    let loginBookRegionAbbr: string | undefined = undefined;

    var loginBookRegionId: string | undefined =
        pathname.includes('region') || pathname.includes('success')
            ? pathname.split('/')[4]
            : undefined;


    //Find book abbr and bookRegion abbr
    session?.userAccounts?.forEach(({ userAccountRegions, book }: any) => {
        userAccountRegions?.forEach(({ bookRegion }: any) => {
            if (bookRegion?.id === loginBookRegionId) {
                loginBookAbbr = book?.abbr;
                loginBookRegionAbbr = bookRegion.abbr;
            }
        });
    });


    // util method: get user account region based on selected country and state options
    const getUserAccountRegionForSelectedLocation = (
        userAccountRegions: IUserAccountRegion[] | undefined
    ): IUserAccountRegion | undefined => {
        return userAccountRegions?.find(
            ({ bookRegion }: IUserAccountRegion) => {
                return bookRegion.abbr === loginBookRegionAbbr;
            }
        );
    };

    // get context subset from url path params
    // the user account is determined by the book chosen (on book button click)
    const selectedUserAccount: IUserAccount | undefined = Boolean(loginBookAbbr)
        ? session?.userAccounts?.find(
              ({ book: { abbr } }: IUserAccount) => abbr === loginBookAbbr
          )
        : undefined;

    const selectedBook: IBook | undefined = selectedUserAccount?.book;


    // the selectedUserAccountRegion is determined by the book and location selected
    const selectedUserAccountRegion = getUserAccountRegionForSelectedLocation(
        selectedUserAccount?.userAccountRegions
    );

    // if the selected bookRegion matches a global bets account (same book different region)
    // then redirect to the existing global account region
    if (session?.globalBetsAccounts) {

        //Does there exist an account with smae book different region?
        let existingGlobalAccount = session?.globalBetsAccounts.find((account) => {
            return account.book.id == selectedUserAccountRegion?.bookRegion.book && account.bookRegion.id != selectedUserAccountRegion?.bookRegion.id
        })

        //If user already has this account linked then we dont want to redirect (avoid infinite redirect loop)
        let currentGlobalAccount = session?.globalBetsAccounts.find((account) => {
            return account.book.id == selectedUserAccountRegion?.bookRegion.book && account.bookRegion.id == selectedUserAccountRegion?.bookRegion.id
        })

        if (existingGlobalAccount && !currentGlobalAccount){
            let redirectBookRegionId = existingGlobalAccount?.bookRegion.id
            let newPath = getLoginFormPagePath(session?.cid || '',redirectBookRegionId)
            if (location.pathname != newPath){
                loginBookRegionId = newPath.includes('region') || newPath.includes('success') ? newPath.split('/')[4] : undefined;
                history.push(newPath)
            }

        }
    }

    let unsupportedReason = getUnsupportedReason(
        selectedBook,
        selectedUserAccountRegion
    );

    //Display Unsupported Reason or Download Extension SWAL
    useEffect(() => {
        if (versionChecked) {
            if (unsupportedReason === UnsupportedReason.DownloadExtension) {
                //NOTE: Need to move this to swal service but having issues with popup when importing
                //This Swal is also managed in the ManageBookCard page
                SwalService.downloadExtensionSwal().then((result) => {
                    if (result.isConfirmed) {
                        window.open(
                            'https://chrome.google.com/webstore/detail/sharpsports/jbjkingkpiakebpokhndnnmniaafoobe',
                            '_blank',
                            'noopener,noreferrer'
                        );
                    }
                });
            } else if (unsupportedReason !== UnsupportedReason.isSupported) {
                //console.log("UNSUPPORTED REASON: ", unsupportedReason)
                //add a log here that we can use for a metric
                logUnsupported(
                    unsupportedReason,
                    selectedBook?.name,
                    session?.user?.name || '',
                    session?.service || '',
                    session?.internalId || ''
                );
                SwalService.unsupportedReasonSwal(
                    unsupportedReason,
                    selectedBook?.name || '',
                    session?.user?.name || ''
                );
            }
        }
    }, [versionChecked]);

    // logging data (for datadog logging)
    const cid = session?.cid;
    const userLog: IUserLog | null = !!session?.user
        ? {
              id: session.user.id,
              name: session.user.name,
          }
        : null;
    const bookLog: Omit<IBookLog, 'status'> | null = !!selectedBook
        ? {
              id: selectedBook.id,
              abbr: selectedBook.abbr as TBookAbbrvs,
              name: selectedBook.name,
          }
        : null;
    const bookRegionLog: IBookRegionLog | null =
        !!selectedUserAccountRegion?.bookRegion
            ? {
                  id: selectedUserAccountRegion.bookRegion.id,
                  abbr: selectedUserAccountRegion.bookRegion.abbr,
                  name: selectedUserAccountRegion.bookRegion.name,
                  isCountry: selectedUserAccountRegion.bookRegion.isCountry,
                  status: selectedUserAccountRegion.bookRegion.status,
              }
            : null;

    // helper functions (wrappers) passed to useBookLogin

    const _logBookLoginSubmit = () => {
        if (!!userLog && !!bookLog && !!bookRegionLog) {
            logBookLoginSubmit(cid!, userLog, bookLog, bookRegionLog);
        } else {
            console.warn(
                'logBookLoginSubmit datadog logger was not fired because required data was undefined'
            );
        }
    };

    const _logBookLoginResponse = (
        responseStatus: LoginStatuses,
        responseTime: number
    ) => {
        if (!!userLog && !!bookLog && !!bookRegionLog) {
            logBookLoginResponse(
                cid!,
                userLog,
                bookLog,
                bookRegionLog,
                responseStatus,
                responseTime
            );
        } else {
            console.warn(
                'logBookLoginResponse datadog logger was not fired because required data was undefined'
            );
        }
    };

    // get methods and data used for book login
    const { handleSubmitLogin, loginStatus, checkLoginStatus } = useBookLogin(
        session?.user?.isSandbox || false,
        session?.cid!,
        session?.user?.id!,
        session?.internalId!,
        session?.token!,
        session?.auth || session?.extensionAuthToken || '',
        extensionVersion,
        session?.extensionAuthToken || '',
        session?.service!,
        session?.user?.name!,
        selectedBook,
        loginBookRegionId,
        loginBookRegionAbbr,
        _logBookLoginSubmit,
        _logBookLoginResponse,
        () => setIsUnverifiedResponseModalOpen(true)
    );

    // debounce such that this function only can trigger once every 300ms
    const handleSubmitLoginClick = useCallback(
        _.debounce(handleSubmitLogin, 300, {
            leading: true,
            trailing: false,
            maxWait: 300,
        }),
        []
    );

    // open tab to make new account on book platform
    const openAffiliateLink = (): void => {
        const userAccount = session?.userAccounts?.find(
            (ua: IUserAccount) => ua?.book?.abbr === loginBookAbbr
        );

        const userAccountRegion = userAccount?.userAccountRegions.find(
            (uar: IUserAccountRegion) =>
                uar?.bookRegion?.id === loginBookRegionId
        );

        let bookLink =
            userAccountRegion?.affiliateLink ||
            userAccount?.affiliateLink ||
            userAccountRegion?.bookRegion?.url ||
            selectedBook?.url;

        if (!bookLink?.match(/^http/)) {
            bookLink = 'https://' + bookLink;
        }

        //for state specific routing from SS book object url
        bookLink = bookLink.replace('ss-state-abbr', loginBookRegionAbbr || '');

        if (!!userLog && !!bookLog && !!bookRegionLog) {
            logOpenAffiliateLink(
                cid!,
                userLog,
                bookLog,
                bookRegionLog,
                bookLink,
                session?.user?.isSandbox || false
            );
        } else {
            console.warn(
                'logOpenAffiliateLink datadog logger was not fired because required data was not defined'
            );
        }
        window.open(bookLink);
    };

    let deepLink =
        (selectedBook?.deepLinkMobile && session?.service) ||
        (selectedBook?.deepLinkExtension && session?.extensionAuthToken);
    let realDeepLink = deepLink && !session?.user?.isSandbox;
    let sandboxDeepLink = deepLink && session?.user?.isSandbox;

    var textHeader = 'Enter your credentials.';
    var textButton = 'Login';
    if (!session?.service && !session?.extensionAuthToken) {
        //pass
    } else if (sandboxDeepLink) {
        textHeader = `In live mode, users will be prompted to enter their credentials directly to the ${selectedBook?.name} site.`;
    } else if (realDeepLink) {
        textHeader = `You will be redirected to the ${selectedBook?.name} website to enter your credentials`;
        textButton = 'Continue';
    }

    if (loginBookAbbr === 'fl') {
        textHeader = 'Enter your phone number';
    }

    const linkStyle = {
        textDecoration: 'underline',
    };

    const PrivacyPolicyLink = () => {
        return (
            <a
                href={PageUrls.PrivacyPolicy}
                target="_blank"
                rel="noopener noreferrer"
                style={linkStyle}
            >
                Privacy Policy
            </a>
        );
    };

    const TermsLink = () => {
        return (
            <a
                href={PageUrls.TermsOfService}
                target="_blank"
                rel="noopener noreferrer"
                style={linkStyle}
            >
                Terms of Use
            </a>
        );
    };

    const promoText = ((): string | null => {
        const userAccount = session?.userAccounts?.find(
            (ua: IUserAccount) => ua?.book?.abbr === loginBookAbbr
        );

        const userAccountRegion = userAccount?.userAccountRegions.find(
            (uar: IUserAccountRegion) =>
                uar?.bookRegion?.id === loginBookRegionId
        );

        return userAccountRegion?.promoText || userAccount?.promoText || null;
    })();

    return (
        <Layout
            colorMode={colorMode}
            alert={session?.user?.isSandbox ? 'SANDBOX MODE' : ''}
            alertSecondary={
                session?.user?.isSandbox && selectedBook?.sdkRequired
                    ? 'For SDK required books please test in live mode and follow implementation instructions in the docs'
                    : ''
            }
            isForm
            onSubmit={handleSubmitLoginClick}
            buttons={
                <>
                    <Button
                        isLoading={loginStatus === LoginStatuses.InProgress}
                        isDisabled={
                            unsupportedReason !== UnsupportedReason.isSupported
                        }
                        type="submit"
                        w="full"
                    >
                        {textButton}
                    </Button>
                    <Button
                        onClick={openAffiliateLink}
                        w="full"
                        variant="secondary"
                    >
                        Create Account
                    </Button>
                    {promoText && (
                        <Text
                            size="md"
                            lineHeight="18px"
                            mb="8px"
                            mt="-8px"
                            align="center"
                        >
                            {promoText}
                        </Text>
                    )}
                </>
            }
            hasBackButton={session?.user?.backButton}
        >
            <Flex
                direction="column"
                align="center"
                gap="32px"
                w="full"
                textAlign="center"
            >
                <Image
                    h="40px"
                    maxW="50%"
                    src={Logos[colorMode][selectedBook?.abbr!] || ''}
                    alt={selectedBook?.name}
                />
                <Heading>{textHeader}</Heading>
                {!realDeepLink && (
                    <Flex direction="column" w="full" gap="16px">
                        <FormControl>
                            <Input
                                placeholder={getInputLabel(
                                    selectedBook?.abbr || ''
                                )}
                                type="text"
                                isDisabled={
                                    unsupportedReason !==
                                        UnsupportedReason.isSupported ||
                                    loginStatus === LoginStatuses.InProgress
                                }
                            />
                            <FormHelperText
                                color={colorMode == 'light' ? 'navy' : 'white'}
                                fontSize="12px"
                                lineHeight="28px"
                                mt="6px"
                            >
                                We'll never share your login information with
                                anyone else.
                            </FormHelperText>
                        </FormControl>
                        {selectedBook?.abbr != 'fl' && (
                            <FormControl>
                                <InputPassword
                                    placeholder="Password"
                                    isDisabled={
                                        unsupportedReason !==
                                            UnsupportedReason.isSupported ||
                                        loginStatus === LoginStatuses.InProgress
                                    }
                                />
                                <FormHelperText
                                    color={
                                        colorMode == 'light' ? 'navy' : 'white'
                                    }
                                    fontSize="12px"
                                    lineHeight="28px"
                                    mt="6px"
                                >
                                    All credentials are encrypted in transit and
                                    storage.
                                </FormHelperText>
                            </FormControl>
                        )}
                    </Flex>
                )}
                <Text size="sm" lineHeight="18px" mb="40px" mt="-8px">
                    By clicking "{textButton}" you agree to the SharpSports{' '}
                    <PrivacyPolicyLink /> and <TermsLink />. You also agree that
                    you are granting us permission to login to your account and
                    share your data with {session?.user?.name}, who may use your
                    data in accordance with its Privacy Policy, including to
                    send you personalized marketing communications. If you do
                    not want us to link your account and share your data with{' '}
                    {session?.user?.name}, please do not click "{textButton}".
                </Text>
            </Flex>

            <UnverifiedResponseModal
                isOpen={isUnverifiedResponseModalOpen}
                onClose={() => setIsUnverifiedResponseModalOpen(false)}
                bookRegionId={loginBookRegionId!}
                token={session?.token!}
                internalId={session?.internalId!}
            />
        </Layout>
    );
}
